import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trophy, Star, Target, Award, Zap, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONE_ICONS = {
  'First Shift': Star,
  'Rising Star': Zap,
  'Dedicated Carer': Award,
  'Team Champion': Crown,
  'Care Legend': Trophy,
};

const MILESTONE_COLORS = {
  'First Shift': 'text-blue-400',
  'Rising Star': 'text-amber-400',
  'Dedicated Carer': 'text-[#A8E6CF]',
  'Team Champion': 'text-purple-400',
  'Care Legend': 'text-[#FF6F61]',
};

export default function CaregiverRewards() {
  const [data, setData] = useState({ rewards: [], total_points: 0, milestones: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/caregiver-management/my-rewards`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const nextMilestone = data.milestones.find(m => !m.achieved);
  const progress = nextMilestone
    ? Math.min(100, (data.total_points / nextMilestone.points_required) * 100)
    : 100;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-rewards-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Rewards & Milestones</h1>
          <p className="text-sm text-muted-foreground mt-1">Track your achievements and points</p>
        </div>

        {/* Points Summary */}
        <Card className="glass-card border-border overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="p-4 rounded-2xl bg-[#FF6F61]/10">
                <Trophy className="w-10 h-10 text-[#FF6F61]" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="text-sm text-muted-foreground">Total Points</p>
                <p className="text-4xl font-heading font-bold text-[#FF6F61]" data-testid="total-points">
                  {data.total_points}
                </p>
                {nextMilestone && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {nextMilestone.points_required - data.total_points} points to {nextMilestone.name}
                  </p>
                )}
              </div>
              {nextMilestone && (
                <div className="w-full sm:w-48">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{data.total_points}</span>
                    <span>{nextMilestone.points_required}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF6F61] rounded-full transition-all duration-700"
                      style={{ width: `${progress}%` }} data-testid="progress-bar" />
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-1">Next: {nextMilestone.name}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Milestones */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Target className="w-5 h-5 text-[#00A6A0]" /> Milestones
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {data.milestones.map(milestone => {
                const Icon = MILESTONE_ICONS[milestone.name] || Star;
                const color = MILESTONE_COLORS[milestone.name] || 'text-muted-foreground';
                return (
                  <div key={milestone.name}
                    className={`p-4 rounded-xl border transition-all duration-200 text-center ${
                      milestone.achieved
                        ? 'border-[#00A6A0]/30 bg-[#00A6A0]/5'
                        : 'border-border bg-muted/30 opacity-50'
                    }`}
                    data-testid={`milestone-${milestone.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className={`w-8 h-8 mx-auto mb-2 ${milestone.achieved ? color : 'text-muted-foreground'}`} />
                    <p className={`text-sm font-medium ${milestone.achieved ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {milestone.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{milestone.points_required} pts</p>
                    {milestone.achieved && (
                      <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full bg-[#A8E6CF]/10 text-[#A8E6CF]">
                        Achieved
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Rewards History */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" /> Rewards History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.rewards.length === 0 ? (
              <div className="p-8 text-center">
                <Star className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No rewards yet. Keep up the great work!</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.rewards.map(reward => (
                  <div key={reward.id} className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                    data-testid={`reward-${reward.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-400/10">
                        <Star className="w-4 h-4 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{reward.title || reward.reason || 'Reward'}</p>
                        <p className="text-xs text-muted-foreground">
                          {reward.created_at ? new Date(reward.created_at).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-[#FF6F61]">+{reward.points || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
